import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { EImageVariant } from '@onesource/schemas';
import type { ProcessServerConfigFunction } from 'filepond';
import { useCallback, useState } from 'react';
import { MdUpload } from 'react-icons/md';

import { DialogTitle } from '../dialogs/dialog-title';
import { FileUploader, type EMimeType } from '../file-uploader/file-uploader';
import { useGetByDevice } from '../hooks/use-responsive';

type TUploadScope = 'personal' | 'system';

type TMediaUploaderDialogProps = {
  allowedFormats: EMimeType[];
  onUploaded: (key: string, isSystem: boolean) => void;
  /** Maximum number of files that can be uploaded at once. Defaults to 20. */
  maxFiles?: number;
  /**
   * Factory that builds a process handler for the given enabled variants.
   * Use this (instead of `processHandler`) to enable the image variant checkboxes.
   */
  processHandlerFactory?: (enabledVariants: EImageVariant[]) => ProcessServerConfigFunction;
  /** Direct process handler — use when variant selection is not needed. */
  processHandler?: ProcessServerConfigFunction;
  /** Initial set of enabled variants. Defaults to all variants. */
  imageVariants?: EImageVariant[];
  /** Whether variant checkboxes are interactive (admin) or read-only (user). Defaults to false. */
  variantsEditable?: boolean;
  /**
   * When true, shows a System / Personal toggle inside the upload dialog.
   * Only show this for admin / kit_organizer users — the API also enforces this server-side.
   */
  showSystemToggle?: boolean;
};

export function MediaUploaderDialog({
  allowedFormats,
  processHandlerFactory,
  processHandler,
  onUploaded,
  maxFiles = 20,
  imageVariants,
  variantsEditable,
  showSystemToggle,
}: TMediaUploaderDialogProps) {
  const [open, setOpen] = useState(false);
  // With the System / Personal toggle there is no default: the user must pick, so a file is never
  // saved to the wrong library by accident. Without the toggle every upload is personal.
  const [chosenScope, setChosenScope] = useState<TUploadScope | null>(null);
  const [uploading, setUploading] = useState(false);
  const uploadScope: TUploadScope | null = showSystemToggle ? chosenScope : 'personal';
  const { isMobile } = useGetByDevice();
  const uploaderColumns = isMobile ? 2 : 3;
  const theme = useTheme();

  const handleOpen = useCallback(() => {
    setChosenScope(null);
    setOpen(true);
  }, []);

  // Esc, a click outside and the close button all land here; none may close the dialog mid-upload.
  const handleClose = useCallback(() => {
    if (uploading) {
      return;
    }
    setOpen(false);
  }, [uploading]);

  const handleUploaded = useCallback(
    (key: string) => onUploaded(key, uploadScope === 'system'),
    [onUploaded, uploadScope],
  );

  return (
    <>
      <Button variant="contained" startIcon={<MdUpload />} onClick={handleOpen}>
        Upload
      </Button>

      <Dialog open={open} onClose={handleClose} disableEscapeKeyDown={uploading} maxWidth="sm" fullWidth>
        <DialogTitle onClose={handleClose} closeDisabled={uploading}>
          Upload Files
        </DialogTitle>
        <DialogContent>
          {showSystemToggle && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Upload as:
              </Typography>
              {(['personal', 'system'] as TUploadScope[]).map(s => (
                <IconButton
                  key={s}
                  size="small"
                  color={uploadScope === s ? 'primary' : 'default'}
                  disabled={uploading}
                  onClick={() => setChosenScope(s)}
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    borderRadius: 1,
                    px: 1.5,
                    border: `1px solid ${uploadScope === s ? theme.palette.primary.main : 'transparent'}`,
                    bgcolor: uploadScope === s ? `${theme.palette.primary.main}14` : 'transparent',
                  }}
                >
                  {s === 'personal' ? 'Personal' : 'System'}
                </IconButton>
              ))}
            </Box>
          )}
          {uploadScope === null && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              Choose Personal or System before selecting files.
            </Typography>
          )}
          <FileUploader
            allowedFormats={allowedFormats}
            maxFiles={maxFiles}
            processHandlerFactory={processHandlerFactory}
            processHandler={processHandler}
            onUploaded={handleUploaded}
            disabled={uploadScope === null}
            onBusyChange={setUploading}
            dropzoneHeight="228px"
            imageVariants={imageVariants}
            variantsEditable={variantsEditable}
            uploaderColumns={uploaderColumns}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
